import React, { useState, useEffect } from 'react';
import { api } from '../../utils/api';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Typography,
    IconButton,
    Tooltip,
    Menu,
    MenuItem,
    Avatar,
    Box,
    ListItemIcon,
    Button,
    Divider
} from '@mui/material';
import { useAccount } from '../../contexts/AccountContext';
import AccountCircle from '@mui/icons-material/AccountCircle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CloseIcon from '@mui/icons-material/Close';
import { googleLogout } from "@react-oauth/google";
import { useNavigate } from 'react-router-dom';
import QRCode from 'react-qr-code';
import DownloadIcon from '@mui/icons-material/Download';


const Profile = () => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [qrDialogOpen, setQrDialogOpen] = useState(false);
    const open = Boolean(anchorEl);
    const { userData, setUserData, currentAccountId } = useAccount();
    const [currentAccount, setCurrentAccount] = useState(null);
    const [accounts, setAccounts] = useState([]);
    const [barSlug, setBarSlug] = useState(null);
    const navigate = useNavigate();
    const [copied, setCopied] = useState(false);
    const [qrCodeRef, setQrCodeRef] = useState(null);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleLogout = () => {
        // Clear all auth-related data from localStorage
        localStorage.clear(); // Clear all localStorage to ensure no auth data remains

        googleLogout();
        if (window.google?.accounts?.id) {
            window.google.accounts.id.revoke();
            window.google.accounts.id.disableAutoSelect();
        }

        setUserData(null);

        // Redirect to home page
        navigate('/', { replace: true });
    };

    const getBarSlug = async () => {
        try {
            const requestUrl = `/api/metrics/bar/${currentAccountId}/slug`;
            const response = await api.get(requestUrl);
            setBarSlug(response.slug);
        } catch (error) {
            console.error('Error fetching slug:', error);
            if (error.response) {
                console.error('Error response data:', error.response.data);
                console.error('Error response status:', error.response.status);
                console.error('Error response headers:', error.response.headers);
            }
        }
    };

    useEffect(() => {
        const getCurrentAccount = async () => {
            const response = await api.get(`/api/users/accounts/${userData.email}`);
            setAccounts(response);
        };

        const fetchData = async () => {
            if (userData?.email) {
                await getCurrentAccount();
            }
            if (currentAccountId) {
                await getBarSlug();
            }
        };

        fetchData();


    }, [userData, currentAccountId]);

    useEffect(() => {
        if (accounts.length > 0 && currentAccountId) {
            setCurrentAccount(accounts.find(acc => acc.id === currentAccountId));
        }
    }, [accounts, currentAccountId]);

    const handleViewSignUpForm = () => {
        if (barSlug) {
            window.open(`${process.env.REACT_APP_URL_frontend}/join/${barSlug}`, '_blank');
        }
    };

    const handleCopy = () => {
        const url = `${process.env.REACT_APP_URL_frontend}/join/${barSlug || 'invalid-slug'}`;
        navigator.clipboard.writeText(url).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    const handleOpenInNewTab = () => {
        const url = `${process.env.REACT_APP_URL_frontend}/join/${barSlug || 'invalid-slug'}`;
        window.open(url, '_blank');
    };

    const handleQrDialogOpen = () => {
        setQrDialogOpen(true);
    };

    const handleQrDialogClose = () => {
        setQrDialogOpen(false);
    };

    const handleDownloadQR = () => {
        if (qrCodeRef) {
            const svg = qrCodeRef.querySelector('svg');
            const svgData = new XMLSerializer().serializeToString(svg);
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            const img = new Image();
            
            img.onload = () => {
                canvas.width = img.width;
                canvas.height = img.height;
                ctx.drawImage(img, 0, 0);
                const pngFile = canvas.toDataURL('image/png');
                const downloadLink = document.createElement('a');
                downloadLink.download = `${currentAccount?.barName || 'bar'}-qr-code.png`;
                downloadLink.href = pngFile;
                downloadLink.click();
            };
            
            img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
        }
    };

    return (
        <>
            <Box
                onClick={handleClick}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    cursor: 'pointer',
                }}
                aria-controls={open ? 'profile-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
            >
                <AccountCircle sx={{ color: 'white', width: 48, height: 48 }} />
            </Box>

            <Menu
                id="profile-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                onClick={handleClose}
                PaperProps={{
                    sx: {
                        width: '300px',
                        mt: 1.5,
                        borderRadius: 2,
                        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
                        '& .MuiMenuItem-root': {
                            px: 2,
                            py: 1,
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            },
                        },
                    },
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
                <Box sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                        {currentAccount?.barName || 'Unnamed Bar'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {userData?.email}
                    </Typography>

                    <Divider sx={{ my: 2 }} />

                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: 1,
                    }}>
                        <Typography
                            variant="body1"
                            onClick={handleViewSignUpForm}
                            sx={{
                                cursor: 'pointer',
                                '&:hover': { color: '#2B0063' },
                                flexGrow: 1,
                                textAlign: 'center',
                                pl: 5
                            }}
                        >
                            View Sign Up Form
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0 }}>
                            <Tooltip title="Copy to clipboard">
                                <IconButton
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleCopy();
                                    }}
                                    sx={{ '&:hover': { color: '#2B0063' } }}
                                >
                                    <ContentCopyIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Open in new tab">
                                <IconButton
                                    onClick={handleOpenInNewTab}
                                    sx={{ '&:hover': { color: '#2B0063' } }}
                                >
                                    <OpenInNewIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Box>

                    {copied && <Typography variant="body2" color="success.main">Copied!</Typography>}

                    <Typography
                        variant="body1"
                        onClick={handleQrDialogOpen}
                        sx={{
                            cursor: 'pointer',
                            mb: 2,
                            '&:hover': { color: '#2B0063' }
                        }}
                    >
                        View / Download QR Code
                    </Typography>

                    <Typography 
                        variant="caption" 
                        color="text.secondary" 
                        sx={{ 
                            mb: 2,
                            fontStyle: 'italic'
                        }}
                    >
                        Questions?{' '}
                        <Box
                            component="span"
                            onClick={() => window.location.href = 'mailto:support@lastcallforbars.com'}
                            sx={{
                                textDecoration: 'underline',
                                cursor: 'pointer',
                                '&:hover': {
                                    color: '#2B0063'
                                }
                            }}
                        >
                            support@lastcallforbars.com
                        </Box>
                    </Typography>

                    <Divider sx={{ my: 2 }} />

                    <Typography
                        onClick={handleLogout}
                        sx={{
                            color: '#2B0063',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            '&:hover': {
                                opacity: 0.9
                            },
                            fontSize: '20px'
                        }}
                    >
                        LOG OUT
                    </Typography>
                </Box>
            </Menu>

            <Dialog
                open={qrDialogOpen}
                onClose={handleQrDialogClose}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6">QR Code</Typography>
                        <IconButton onClick={handleQrDialogClose}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>

                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
                        {barSlug ? (
                            <>
                                <Typography variant="h6" sx={{ mb: 2 }}>
                                    Scan to join {currentAccount?.barName || 'the bar'}
                                </Typography>
                                <Box 
                                    ref={setQrCodeRef}
                                    sx={{ 
                                        p: 2, 
                                        bgcolor: 'white', 
                                        borderRadius: 1,
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        gap: 2
                                    }}
                                >
                                    <QRCode
                                        value={`${process.env.REACT_APP_URL_frontend}/join/${barSlug}`}
                                        size={200}
                                        level="H"
                                        includeMargin={true}
                                    />
                                    <Button
                                        variant="contained"
                                        startIcon={<DownloadIcon />}
                                        onClick={handleDownloadQR}
                                        sx={{
                                            bgcolor: '#2B0063',
                                            '&:hover': {
                                                bgcolor: '#1a0040'
                                            }
                                        }}
                                    >
                                        Download QR Code
                                    </Button>
                                </Box>
                            </>
                        ) : (
                            <Typography>Loading QR code...</Typography>
                        )}
                    </Box>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default Profile; 